import { createAsyncThunk } from '@reduxjs/toolkit'
import AxiosClient from '../AxiosClient'
import { OMS_BASE } from 'src/utils/constants'

// ===================================================================
// Order Package APIs — CRUD for packages tied to a specific order
// ===================================================================

/**
 * Create a new order-package (assigns items from an order into a package)
 * POST /oms/order-packages
 */
export const createOrderPackageAsync = createAsyncThunk(
  'oms/createOrderPackageAsync',
  async function (payload: any, toolkit) {
    const url = `${OMS_BASE}/order-packages`
    return await AxiosClient('POST', url, payload, toolkit, [])
  }
)

/**
 * Update an existing order-package by its ID
 * PUT /oms/order-packages/:id
 */
export const updateOrderPackageAsync = createAsyncThunk(
  'oms/updateOrderPackageAsync',
  async function (payload: { id: number; payloadupdate: any }, toolkit) {
    const url = `${OMS_BASE}/order-packages/${payload?.id}`
    return await AxiosClient('PUT', url, payload.payloadupdate, toolkit, [])
  }
)

/**
 * Fetch a single order-package by its ID (used when clicking Edit on a card)
 * GET /oms/order-packages?id=:id
 */
export const getOrderPackageDetailAsync = createAsyncThunk(
  'oms/getOrderPackageDetailAsync',
  async function (id: number, toolkit) {
    const url = `${OMS_BASE}/order-packages?id=${id}`
    return await AxiosClient('GET', url, [], toolkit, [], toolkit.signal)
  }
)

/**
 * Fetch order-package history for an order (with search, pagination, filters)
 * GET /oms/order-packages?order_id=...&search=...&page=...
 */
export const getOrderPackageListAsync = createAsyncThunk(
  'oms/getOrderPackageListAsync',
  async function (
    params: {
      page: number
      limit?: number
      order_by?: string
      sort_by?: string
      search?: string
      order_id?: number
      id?: number
      is_active?: boolean
    },
    toolkit
  ) {
    const query = new URLSearchParams()
    // Required
    query.append('page', String(params.page))
    // Optional
    if (params.limit) query.append('limit', String(params.limit))
    if (params.order_by) query.append('order_by', params.order_by)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.search) query.append('search', params.search)
    if (params.order_id) query.append('order_id', String(params.order_id))
    if (params.id) query.append('id', String(params.id))
    if (params.is_active !== undefined) query.append('is_active', String(params.is_active))

    const url = `${OMS_BASE}/order-packages?${query.toString()}`
    return await AxiosClient('GET', url, [], toolkit, [], toolkit.signal)
  }
)

/**
 * Fetch available order line items for packaging (with search, pagination)
 * GET /oms/order-lines-for-package/:orderId?search=...&page=...
 */
export const getOrderLinesForPackageAsync = createAsyncThunk(
  'oms/getOrderLinesForPackageAsync',
  async function (
    params: {
      orderId: number
      page: number
      limit?: number
      order_by?: string
      sort_by?: string
      search?: string
      direct_shipment?: boolean
    },
    toolkit
  ) {
    const query = new URLSearchParams()
    // Required
    query.append('page', String(params.page))
    // Optional
    if (params.limit) query.append('limit', String(params.limit))
    if (params.order_by) query.append('order_by', params.order_by)
    if (params.sort_by) query.append('sort_by', params.sort_by)
    if (params.search) query.append('search', params.search)
    if (params.direct_shipment !== undefined) query.append('direct_shipment', String(params.direct_shipment))

    const url = `${OMS_BASE}/order-lines-for-package/${params.orderId}?${query.toString()}`
    return await AxiosClient('GET', url, [], toolkit, [], toolkit.signal)
  }
)

/**
 * Delete an order-package by its ID
 * DELETE /oms/order-packages/:id
 */
export const deleteOrderPackageAsync = createAsyncThunk(
  'oms/deleteOrderPackageAsync',
  async function (payload: { id: number }, toolkit) {
    const url = `${OMS_BASE}/order-packages/${payload.id}`
    return await AxiosClient('DELETE', url, [], toolkit, [])
  }
)
